/**
 * Stock quantities at the edge (FR-116, research.md R4).
 *
 * Every quantity inside the system is an INTEGER in thousandths of its
 * ingredient's unit: 250 ml of milk stocked in litres is 250, a 1 kg bag of
 * beans is 1000. This file is the only place a person's "0.25" becomes 250 or
 * 250 becomes "0.25", and it does both with string arithmetic — so "0.1" is
 * exactly 100, never 100.00000000000001, and nothing downstream ever sees a
 * float it would have to round.
 */

export const UNITS = ["g", "kg", "ml", "l", "piece"] as const;
export type Unit = (typeof UNITS)[number];

export const UNIT_LABELS: Record<Unit, string> = {
  g: "grams",
  kg: "kilograms",
  ml: "millilitres",
  l: "litres",
  piece: "pieces",
};

const SCALE = 1000;
const DECIMALS = 3;

export class QuantityError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "QuantityError";
  }
}

export function isUnit(value: unknown): value is Unit {
  return typeof value === "string" && (UNITS as readonly string[]).includes(value);
}

/**
 * "0.25" → 250. Refuses anything finer than a thousandth rather than rounding
 * it away — a recipe that genuinely needs finer resolution should use a
 * smaller unit, not quietly lose the difference on every sale.
 */
export function parseQuantity(text: string, options: { allowNegative?: boolean } = {}): number {
  const trimmed = text.trim();
  const match = /^(-)?(\d+)?(?:\.(\d+))?$/.exec(trimmed);

  if (!match || (match[2] === undefined && match[3] === undefined)) {
    throw new QuantityError(`"${text}" is not a quantity.`);
  }

  const [, sign, whole = "0", fraction = ""] = match;

  if (fraction.length > DECIMALS) {
    throw new QuantityError(`"${text}" is finer than a thousandth. Use a smaller unit.`);
  }
  if (sign && !options.allowNegative) {
    throw new QuantityError(`"${text}" cannot be negative here.`);
  }

  const value = Number(whole) * SCALE + Number(fraction.padEnd(DECIMALS, "0"));
  if (!Number.isSafeInteger(value)) {
    throw new QuantityError(`"${text}" is too large.`);
  }

  return sign && value !== 0 ? -value : value;
}

/** 19800 → "19.8". The number alone, for an input's value. */
export function formatAmount(thousandths: number): string {
  const negative = thousandths < 0;
  const magnitude = Math.abs(thousandths);
  const whole = Math.floor(magnitude / SCALE);
  const fraction = magnitude % SCALE;

  const text =
    fraction === 0
      ? String(whole)
      : `${whole}.${String(fraction).padStart(DECIMALS, "0").replace(/0+$/, "")}`;

  return negative ? `-${text}` : text;
}

/** 19800, "l" → "19.8 l". */
export function formatQuantity(thousandths: number, unit: Unit): string {
  return `${formatAmount(thousandths)} ${unit === "piece" ? "pc" : unit}`;
}

// ---------------------------------------------------------------------------
// Recipe entry — the unit people actually think in
// ---------------------------------------------------------------------------

/**
 * Units whose thousandth has its own everyday name. Nobody writes "0.2 l" of
 * milk in a latte; they write 200 ml — and for a litre ingredient 200 ml IS
 * the stored integer, so recipe lines are typed and shown in it directly.
 */
const THOUSANDTH_NAME: Partial<Record<Unit, string>> = { l: "ml", kg: "g" };

/** "l" → "ml", "kg" → "g", "piece" → "pc", anything else unchanged. */
export function entryUnit(unit: Unit): string {
  return THOUSANDTH_NAME[unit] ?? (unit === "piece" ? "pc" : unit);
}

/**
 * A recipe quantity as typed: whole millilitres or grams for a litre or
 * kilogram ingredient (a fraction of either is finer than the store keeps),
 * otherwise the unit itself to three decimals. Negative only when allowed —
 * an add-on's substitute line.
 */
export function parseEntry(text: string, unit: Unit, options: { allowNegative?: boolean } = {}): number {
  if (!THOUSANDTH_NAME[unit]) return parseQuantity(text, options);

  const match = /^(-)?(\d+)$/.exec(text.trim());
  if (!match) {
    throw new QuantityError(`"${text}" should be a whole number of ${entryUnit(unit)}.`);
  }
  const [, sign, digits = "0"] = match;
  if (sign && !options.allowNegative) {
    throw new QuantityError(`"${text}" cannot be negative here.`);
  }

  const value = Number(digits);
  if (!Number.isSafeInteger(value)) throw new QuantityError(`"${text}" is too large.`);
  return sign && value !== 0 ? -value : value;
}

/** 200, "l" → "200 ml"; 1500, "g" → "1.5 g". */
export function formatEntry(thousandths: number, unit: Unit): string {
  return THOUSANDTH_NAME[unit]
    ? `${thousandths} ${entryUnit(unit)}`
    : `${formatAmount(thousandths)} ${entryUnit(unit)}`;
}

// ---------------------------------------------------------------------------
// Delivery prices — pesos typed, centavos stored
// ---------------------------------------------------------------------------

/**
 * "85.50" → 8550. A money path, so it gets the same treatment as quantities:
 * string arithmetic, no float, and a fraction of a centavo is refused rather
 * than rounded. Thousands may be grouped with commas ("1,250.75"); a lone comma
 * ("1,25") is ambiguous and refused.
 */
export function parsePesos(text: string): number {
  const match = /^(\d{1,3}(?:,\d{3})+|\d+)(?:\.(\d{1,2}))?$/.exec(text.trim());
  if (!match) throw new QuantityError(`"${text}" is not an amount in pesos.`);

  const [, whole = "0", fraction = ""] = match;
  const value = Number(whole.replace(/,/g, "")) * 100 + Number(fraction.padEnd(2, "0"));
  if (!Number.isSafeInteger(value)) throw new QuantityError(`"${text}" is too large.`);
  return value;
}

/** 8550 → "85.50". Display only; no currency symbol, which is the deployment's to choose. */
export function formatPesos(centavos: number): string {
  const magnitude = Math.abs(centavos);
  const text = `${Math.floor(magnitude / 100).toLocaleString("en-US")}.${String(magnitude % 100).padStart(2, "0")}`;
  return centavos < 0 ? `-${text}` : text;
}
